import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { useQueryClient } from '@tanstack/react-query';
import Cookie from 'js-cookie';
import { useCallback } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useRouter } from 'shared-routing';

import { Routes } from '@common/constants';
import { invalidateSpaPackageTimeslots } from '@features/spa';
import { getSpaPackageTimeslots } from '@features/spa/hooks/useGetSpaPackageTimeslots';
import { useReturnToTimeWhenSlotTaken } from '@features/spa/hooks/useReturnToTimeWhenSlotTaken';
import { createBasket, updateBasket } from '@store/basket/actions';
import { basketSelectors } from '@store/basket/selectors';
import { SpaPackageType } from 'shared-types/SpaPackage';

import { SpaPackageFormValues } from '../../SpaPackageForm.schema';
import {
  SpaPackageBasketResolveType,
  SpaPackageFormDataType,
} from '../../SpaPackageModal/SpaPackageModal.types';
import { parseSpaPackageData } from '../../SpaPackageModal/SubmitButton/SubmitButton.helpers';

type UseSubmitBookingParams = {
  form: UseFormReturn<SpaPackageFormValues>;
  entity: SpaPackageType | undefined;
  propertyId: number;
  language: string;
  id: number;
  onClose: (showMiniBasket: boolean) => void;
  onInvalid?: () => void;
};

export function useSubmitBooking({
  form,
  entity,
  propertyId,
  language,
  id,
  onClose,
  onInvalid,
}: UseSubmitBookingParams) {
  const dispatch = useDispatch();
  const router = useRouter();
  const queryClient = useQueryClient();
  const isGroupedSpaBookingEnabled = useFeatureIsOn('spa-days-grouped-booking');
  const currentBasketItems = useSelector(
    (state: Record<string, unknown>) =>
      (basketSelectors.getBasketDetails(state) as { items?: unknown[] })?.items || []
  );

  const quantity = entity?.numberOfGuests ?? 1;
  const isStillOffered = useCallback(
    async (date: string, arrivalTime: NonNullable<SpaPackageFormValues['arrivalTime']>) => {
      const slots = await getSpaPackageTimeslots({
        spaPackageId: id,
        startDate: date,
        quantity,
        propertyId,
        numberOfDays: 1,
      });
      const daySlots = slots[date];
      return (
        Array.isArray(daySlots) && daySlots.some((slot) => slot.startTime === arrivalTime.startTime)
      );
    },
    [id, quantity, propertyId]
  );

  const returnToTimeIfSlotTaken = useReturnToTimeWhenSlotTaken({
    form: form as never,
    id,
    bookingRoute: Routes.SPA_DAY_BOOKING,
    isStillOffered,
  });

  const addToBasket = useCallback(
    (data: SpaPackageFormValues) => {
      if (!entity) return;
      const cleanedExtras = Object.fromEntries(
        Object.entries(data.extras || {}).filter(([, v]) => Array.isArray(v) && v.length > 0)
      );
      const cleanedData = { ...data, extras: cleanedExtras };
      const parsedBasketItems = parseSpaPackageData(
        cleanedData as unknown as SpaPackageFormDataType,
        entity,
        isGroupedSpaBookingEnabled
      );
      const promises: SpaPackageBasketResolveType[] = [];
      if (currentBasketItems.length) {
        const basketId = Cookie.get('basketId') as string;
        const items = [...currentBasketItems, ...parsedBasketItems];
        promises.push(
          dispatch(
            updateBasket(propertyId, basketId, language, { items }, false, true) as any
          ) as unknown as SpaPackageBasketResolveType
        );
      } else {
        promises.push(
          dispatch(
            createBasket(propertyId, language, { items: parsedBasketItems }, true) as any
          ) as unknown as SpaPackageBasketResolveType
        );
      }
      Promise.any(promises)
        .then(() => onClose(true))
        .catch(() => returnToTimeIfSlotTaken())
        .finally(() => invalidateSpaPackageTimeslots(queryClient));
    },
    [
      entity,
      isGroupedSpaBookingEnabled,
      currentBasketItems,
      dispatch,
      propertyId,
      language,
      queryClient,
      onClose,
      returnToTimeIfSlotTaken,
    ]
  );

  const handleSubmit = form.handleSubmit(addToBasket, onInvalid);

  const handleNextStep = form.handleSubmit(() => {
    router.push({
      pathname: Routes.SPA_DAY_BOOKING,
      query: { id, bookingStep: 'extras' },
    });
  }, onInvalid);

  const handleBack = useCallback(() => {
    router.push({ pathname: `${Routes.SPA_DAY_DETAIL.replace('[id]', String(id))}` });
  }, [router, id]);

  return {
    handleSubmit,
    handleNextStep,
    handleBack,
  };
}
